
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import jszip, { file } from 'jszip';

import Popup from '@/components/utils/Popup.vue';
import { GuestPermission, Room, RoomSpace, SubRoom, UserRoomState } from '@/utils/models';
import {
  addErrorMessage,
  setFocusToInput,
  isGuest,
  uploadFile,
  removeProps,
  getDataServerUrl,
  isAdmin,
} from '@/utils/functions';
import ImageUploader2 from '@/components/utils/ImageUploader2.vue';
import EditSubRoomPopup from './EditSubRoomPopup.vue';
import ConfirmCancellationPopup from '@/components/popup/common/ConfirmCancellationPopup.vue';
import ConfirmEditApplyPopup from '@/components/popup/common/ConfirmEditApplyPopup.vue';
import vSelect from 'vue-select';
import { v4 as uuid } from 'uuid';
import { DataService } from '@/service/DataService';
import dayjs from 'dayjs';
import LoadingPopup from '../room/LoadingPopup.vue';
import { DEFAULT_ENABLE_SCREEN_SHARE_IDLING, DEFAULT_SCREEN_SHARE_SCOPE } from '@/utils/constants';

@Component({
  components: {
    Popup,
    ImageUploader2,
    EditSubRoomPopup,
    ConfirmCancellationPopup,
    ConfirmEditApplyPopup,
    'v-select': vSelect,
    LoadingPopup,
  },
})
export default class EditRoomPopup extends Vue {
  @Prop() room!: Room;
  @Prop() spacesState!: RoomSpace[];
  @Prop() presenceOfPeopleInRoom!: number;

  @Emit() editRoom(target: Room, imageFile: File | null) {}
  @Emit() close() {}

  editingRoom: Room | null = null;
  private imageFile: File | null = null;

  hideImageUploader: boolean = false;
  showEditSubroom = false;
  allowUserEmailsString = '';
  allowUserEmailsError = false;
  adminUsersString = '';
  adminUsersError = false;
  isLoading = false;
  addingRoomErrorMessage = '';
  imageUrl = '';
  private originalRoomStringify: string = '';
  private coppiedRoomStringify: string = '';

  showConfirmCancellation = false;
  settingType: 'general' | 'admission' | 'layout' = 'general';
  shouldShowConfirmEditApplyPopup = false;

  get domains(): string[] {
    return this.$store.state.siteInfo?.allowDomainNames || [];
  }
  get canApply(): boolean {
    return (
      !!this.editingRoom?.name?.trim() &&
      this.parseNewSubRoomPositionInput() &&
      !!this.imageUrl &&
      !this.allowUserEmailsError &&
      !this.adminUsersError &&
      this.originalRoomStringify !== JSON.stringify(this.editingRoom)
    );
  }
  get presenceOfPeople(): number {
    return this.presenceOfPeopleInRoom || 0;
  }

  get isAdmin(): boolean {
    return isAdmin(this.$store.state.user);
  }

  get isTrialSite(): boolean {
    return this.$store.state.siteInfo?.isDemo;
  }

  mounted(): void {
    this.clearAll();
    this.clearAddingRoomErrorMessage();
    this.initNewRoomInput();
    setFocusToInput(this.$refs.popupInputRef);
  }
  beforeDestroy() {
    this.clearImage();
  }
  checkInputAndUpdate() {
    this.clearAddingRoomErrorMessage();
    let emailCheck = /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/;
    let users = this.allowUserEmailsString.split(/[\s\t,]+/).filter((v) => v);
    this.allowUserEmailsError = false;
    if (users.find((u) => !emailCheck.test(u))) {
      this.addingRoomErrorMessage = '許可ユーザーのメールアドレスが不正です';
      this.allowUserEmailsError = true;
    } else if (this.editingRoom) {
      this.editingRoom.allowUserEmails = users;
    }
    let admins = this.adminUsersString.split(/[\s\t,]+/).filter((v) => v);
    this.adminUsersError = false;
    if (admins.find((u) => !emailCheck.test(u))) {
      this.addingRoomErrorMessage += ' 管理者ユーザーのメールアドレスが不正です';
      this.adminUsersError = true;
    } else if (this.editingRoom) {
      this.editingRoom.adminEmails = admins;
    }
  }

  private error(errorMessage: string): void {
    this.addingRoomErrorMessage = errorMessage;
  }

  private clearAll(): void {
    if (!this.room) {
      return;
    }
    const initMinScale = 40;
    if (!this.originalRoomStringify) {
      const clonedRoom = JSON.parse(JSON.stringify(this.room)) as Room;
      clonedRoom.allowDomains = this.room.allowDomains ?? [];
      clonedRoom.allowUserEmails = this.room.allowUserEmails ?? [];
      clonedRoom.defaultVoiceArea = this.room.defaultVoiceArea ?? 450;
      clonedRoom.initialScaleMode = this.room.initialScaleMode ?? 'fitHeight';
      clonedRoom.initialScale = this.room.initialScale ?? initMinScale;
      clonedRoom.minScaleMode = this.room.minScaleMode ?? 'fitHeight';
      clonedRoom.minScale = this.room.minScale ?? initMinScale;
      clonedRoom.enteringSoundVolume = this.room.enteringSoundVolume ?? 0.3;
      clonedRoom.doorSoundVolume = this.room.doorSoundVolume ?? 0.5;
      clonedRoom.spaceId = this.room.spaceId;
      clonedRoom.isPublicRoom = this.room.isPublicRoom ?? false;
      clonedRoom.screenShareScope = this.room.screenShareScope ?? DEFAULT_SCREEN_SHARE_SCOPE;
      clonedRoom.enableScreenShareIdling =
        this.room.enableScreenShareIdling != undefined
          ? this.room.enableScreenShareIdling
          : DEFAULT_ENABLE_SCREEN_SHARE_IDLING;
      this.originalRoomStringify = JSON.stringify(clonedRoom);
    }
    let room: Room = JSON.parse(this.originalRoomStringify);
    this.editingRoom = room;
    room.allowDomains = room.allowDomains ?? [];
    room.allowUserEmails = room.allowUserEmails ?? [];
    room.adminEmails = room.adminEmails ?? [];
    this.allowUserEmailsString = room.allowUserEmails.join(', ');
    this.adminUsersString = room.adminEmails?.join(', ');
    this.clearImage();
    this.imageUrl = room.roomMapUrl;
    this.hideImageUploader = !this.imageUrl;
  }

  private clearAddingRoomErrorMessage(): void {
    this.addingRoomErrorMessage = '';
  }

  private initNewRoomInput(): void {
    if (this.editingRoom) {
      this.hideImageUploader = true;
    } else {
      this.addingRoomErrorMessage = 'ルームが存在しません';
    }
  }

  private parseNewSubRoomPositionInput(): boolean {
    if (!this.editingRoom) {
      return false;
    }
    for (let i = 0; i < this.editingRoom!.subRooms.length; i++) {
      if (
        !this.editingRoom!.subRooms[i].topLeft.x ||
        !this.editingRoom!.subRooms[i].topLeft.y ||
        !this.editingRoom!.subRooms[i].bottomRight.x ||
        !this.editingRoom!.subRooms[i].bottomRight.y
      ) {
        this.error(`${this.editingRoom!.subRooms[i].name}の値が空白です`);
        return false;
      } else if (
        this.editingRoom.roomSize.width <= this.editingRoom!.subRooms[i].bottomRight.x ||
        this.editingRoom.roomSize.height <= this.editingRoom!.subRooms[i].bottomRight.y
      ) {
        this.error(
          `${
            this.editingRoom!.subRooms[i].name
          }が画像（ルーム）サイズの範囲外に存在しています会議室設定で編集してください`,
        );
        return false;
      }
      if (i == this.editingRoom!.subRooms.length) {
        break;
      }
      for (let j = i + 1; j < this.editingRoom!.subRooms.length; j++) {
        /* x値の重なり判定 */
        if (
          this.editingRoom!.subRooms[i].bottomRight.x > this.editingRoom!.subRooms[j].topLeft.x &&
          this.editingRoom!.subRooms[i].topLeft.x < this.editingRoom!.subRooms[j].bottomRight.x
        ) {
          /* y値の重なり判定 */
          if (
            this.editingRoom!.subRooms[i].bottomRight.y > this.editingRoom!.subRooms[j].topLeft.y &&
            this.editingRoom!.subRooms[i].topLeft.y < this.editingRoom!.subRooms[j].bottomRight.y
          ) {
            this.error(
              `${this.editingRoom!.subRooms[i].name} と ${
                this.editingRoom!.subRooms[j].name
              }が重なっています会議室設定で編集してください`,
            );
            return false;
          }
        }
      }
    }
    this.clearAddingRoomErrorMessage();
    return true;
  }

  onApplyImage(imageFile: File) {
    this.clearImage();
    this.imageFile = imageFile;
    this.imageUrl = URL.createObjectURL(imageFile);
    this.editingRoom!.roomMapUrl = this.imageUrl;
    const img = document.createElement('img');
    img.onload = () => {
      if (!this.editingRoom) {
        return;
      }
      this.editingRoom.roomSize.width = img.width;
      this.editingRoom.roomSize.height = img.height;
      this.hideImageUploader = true;
      this.clearAddingRoomErrorMessage();
    };
    img.src = this.imageUrl;
  }
  clearImage() {
    if (this.imageUrl && this.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imageUrl);
    }
    this.imageFile = null;
    this.imageUrl = '';
    this.hideImageUploader = false;
  }

  onConfirm() {
    if (this.canApply && this.editingRoom) {
      const imageFile = this.imageFile;
      this.clearImage();
      this.editRoom(this.editingRoom, imageFile);
    }
  }
  onEditSubroomClosed() {
    this.showEditSubroom = false;
  }
  onAppluSubrooms(subrooms: SubRoom[]) {
    if (this.editingRoom) {
      this.editingRoom.subRooms = subrooms;
    }
    this.showEditSubroom = false;
  }
  hidePopup(): void {
    this.close();
  }
  closeOrShowConfirmCancellation(): void {
    if (this.canApply) {
      this.showConfirmCancellation = true;
    } else {
      this.close();
    }
  }
  closeConfirmCancellation(): void {
    this.showConfirmCancellation = false;
  }
  showConfirmEditApplyPopup(): void {
    this.shouldShowConfirmEditApplyPopup = true;
  }
  closeConfirmEditApplyPopup(): void {
    this.shouldShowConfirmEditApplyPopup = false;
  }
  get spacesList(): RoomSpace[] {
    return this.spacesState?.filter((v) => v.spaceType !== 'trash') ?? [];
  }
  importRoomData() {
    let el = this.$refs.fileSelecter as HTMLInputElement;
    if (!el) {
      return;
    }
    el.value = '';
    el.click();
  }
  async onRoomDataChanged(ev: Event) {
    let el = ev.target as HTMLInputElement;
    if (!el || !el.files?.length || !this.editingRoom) {
      return;
    }
    this.isLoading = true;
    try {
      const roomFile = el.files[0];
      const zip = await jszip.loadAsync(roomFile);
      const roomJson = await zip.file('room.json')?.async('string');
      if (!roomJson) {
        this.$toast.error('指定されたファイルが不正です');
        return;
      }

      const room = JSON.parse(roomJson) as Room;
      const targetRoom = JSON.parse(JSON.stringify(this.editingRoom)) as Room;
      this.assignRoomData(room, targetRoom);
      let bgmFileMap: { [path: string]: File } = {};
      if (room.subRooms?.length) {
        let bgmInfoJson = await zip.file('bgm/bgm.json')?.async('string');
        let bgmInfo: { [path: string]: string } = {};
        if (bgmInfoJson) {
          bgmInfo = JSON.parse(bgmInfoJson);
          const bgmFiles = Object.keys(bgmInfo);
          for (let i = 0; i < bgmFiles.length; i++) {
            let path = bgmFiles[i];
            let filename = bgmInfo[path];
            if (filename) {
              let bgmBlob = await zip.file('bgm/' + filename)?.async('blob');
              if (bgmBlob) {
                bgmFileMap[path] = new File([bgmBlob], filename);
              }
            }
          }
        }
        let subrooms: SubRoom[] = [];
        let currentMap = new Map<string, SubRoom>();
        targetRoom.subRooms.forEach((s) => currentMap.set(s.subid, s));
        room.subRooms.forEach((s) => {
          let target: SubRoom = currentMap.has(s.subid) ? currentMap.get(s.subid) : <any>{};
          if (!target.subid) {
            target.subid = uuid();
          }
          this.assignSubRoomData(s, target);
          subrooms.push(target);
        });
        targetRoom.subRooms = subrooms;
      }
      // ここから更新開始
      const mapFiles = Object.keys(zip.files)
        .filter((k) => k.startsWith('map/') && !zip.files[k].dir)
        .map((k) => zip.files[k]);
      if (mapFiles.length > 0) {
        const mapFile = await mapFiles[0].async('blob');
        const imageFile = new File([mapFile], mapFiles[0].name);
        this.onApplyImage(imageFile);
        targetRoom.roomMapUrl = room.roomMapUrl;
      } else {
        this.clearImage();
        this.imageUrl = room.roomMapUrl;
        targetRoom.roomMapUrl = room.roomMapUrl;
        this.hideImageUploader = true;
      }
      // BGMのアップロード
      for (let i = 0; i < targetRoom.subRooms.length; i++) {
        let subroom = targetRoom.subRooms[i];
        if (!subroom.bgmUrl || !bgmFileMap[subroom.bgmUrl]) {
          continue;
        }
        const bgmFileObj = bgmFileMap[subroom.bgmUrl];
        const url = await uploadFile(this.$store.state.dataService as DataService, 'audio', bgmFileObj);
        subroom.bgmUrl = url;
      }
      this.editingRoom = targetRoom;
      this.allowUserEmailsString = targetRoom.allowUserEmails?.join(', ') ?? '';
      this.adminUsersString = targetRoom.adminEmails?.join(', ') ?? '';
      this.$toast.info('ルーム情報を読み込みました');
    } catch (e) {
      this.$toast.error('ルーム情報の読み込みに失敗しました');
    } finally {
      this.isLoading = false;
    }
  }
  private assignRoomData(from: Room, to: Room) {
    let copyProps: (keyof Room)[] = [
      // 'name', // name と description は一旦適用しない方向で
      // 'description',
      'roomSize',
      'entrancePosition',
      'initialScale',
      'initialScaleMode',
      'minScaleMode',
      'minScale',
      'maxScale',
      'defaultVoiceArea',
      // 'allowDomains', // 権限系も適用しないように変更
      // 'allowUserEmails',
      // 'adminEmails',
      'enteringSoundVolume',
      'doorSoundVolume',
    ];
    copyProps.forEach((key) => {
      if (from[key]) {
        (to as any)[key] = from[key];
      }
    });
  }
  private assignSubRoomData(from: SubRoom, to: SubRoom) {
    let copyProps: (keyof SubRoom)[] = [
      'name',
      'showName',
      'nameStyle',
      'areaStyle',
      'topLeft',
      'bottomRight',
      'volume',
      'bgmUrl',
      'bgmAudioVolume',
    ];
    copyProps.forEach((key) => {
      if (from[key]) {
        (to as any)[key] = from[key];
      }
    });
  }
  async exportRoomData() {
    this.isLoading = true;
    try {
      const zip = new jszip();
      const room: Room = JSON.parse(JSON.stringify(this.editingRoom));
      room.subRooms?.forEach((s) => removeProps(s, 'userStates'));
      removeProps(
        room,
        'id',
        'name',
        'description',
        'spaceId',
        'spaceName',
        'roomStates',
        'guestPermission',
        'allowDomains',
        'allowUserEmails',
        'adminEmails',
      );
      zip.file('room.json', JSON.stringify(room, null, '  '));
      const mapfolder = zip.folder('map');
      if (mapfolder) {
        if (this.imageFile) {
          mapfolder.file(this.imageFile.name, this.imageFile);
        } else if (room.roomMapUrl.startsWith('/zawa-data/')) {
          await this.downloadAndAddZip(mapfolder, room.roomMapUrl);
        }
      }
      let bgmInfo: { [path: string]: string } = {};
      const bgmfolder = zip.folder('bgm');
      for (let sub of room.subRooms) {
        if (bgmfolder && sub.bgmUrl && sub.bgmUrl.startsWith('/zawa-data/')) {
          const bgmfilename = await this.downloadAndAddZip(bgmfolder, sub.bgmUrl);
          bgmInfo[sub.bgmUrl] = bgmfilename;
        }
      }
      if (bgmfolder && Object.keys(bgmInfo).length) {
        bgmfolder.file('bgm.json', JSON.stringify(bgmInfo));
      }
      const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 9,
        },
      });
      const link = document.createElement('a');
      const now = dayjs().format('YYYYMMDD-HHmmss');
      link.href = URL.createObjectURL(blob);
      link.download = `room-${this.editingRoom?.name}_${now}.zawa`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);

      this.$toast.info('ルーム情報のダウンロードを開始しました');
    } catch (e) {
      this.$toast.error('ルーム情報の書き出しに失敗しました');
      console.log('書き出し失敗', e);
    } finally {
      this.isLoading = false;
    }
  }
  private async downloadAndAddZip(zip: jszip, url: string) {
    const dsUrl = getDataServerUrl();
    const fileid = url.substring(url.lastIndexOf('/') + 1);
    const fileinfo = await this.axios.get(`${dsUrl}/zawa-data/api/fileinfo/${fileid}`);

    const res = await this.axios.get(url, { responseType: 'arraybuffer' });
    let filename: string = fileinfo.data?.originalname;
    if (!filename) {
      let ext = fileinfo.data?.mimetype ? fileinfo.data.mimetype.substring('image/'.length) : 'png';
      filename = 'map.' + ext;
    }
    zip.file(filename, res.data);
    return filename;
  }
}
